import React, { useState } from "react";
import { Mail, Pencil, Check, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { BROKER_ROLES, SHIPMENT_TYPES } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const FIELDS = [["sender_name", "Sender"], ["sender_email", "Email"], ["subject", "Subject"], ["mawb_hint", "MAWB hint"], ["origin_hint", "Origin hint"], ["agent_hint", "Agent / forwarder"], ["hawb_hint", "HAWB hint"], ["client_hint", "Client hint"], ["eta_hint", "ETA"], ["flight_hint", "Flight"]];

const Extras = ({ p }) => (
  <>
    {p.priority && (
      <div className="flex justify-between gap-4 py-1.5 border-b border-slate-800/70">
        <dt className="text-xs text-slate-500 uppercase tracking-widest">Priority</dt>
        <dd data-testid="prealert-priority" className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30">{p.priority}</dd>
      </div>
    )}
    {p.references?.length > 0 && (
      <div className="flex justify-between gap-4 py-1.5 border-b border-slate-800/70">
        <dt className="text-xs text-slate-500 uppercase tracking-widest">References</dt>
        <dd data-testid="prealert-references" className="flex flex-wrap justify-end gap-1">
          {p.references.map((r) => <span key={r} className="font-mono-ops text-xs px-1.5 py-0.5 rounded bg-slate-800 text-slate-200">{r}</span>)}
        </dd>
      </div>
    )}
  </>
);

const View = ({ p }) => (
  <dl>
    {FIELDS.map(([k, l]) => (
      <div key={k} className="flex justify-between gap-4 py-1.5 border-b border-slate-800/70 last:border-0">
        <dt className="text-xs text-slate-500 uppercase tracking-widest">{l}</dt>
        <dd data-testid={`prealert-${k}`} className="text-sm text-slate-200 text-right break-all">{p[k] || "—"}</dd>
      </div>
    ))}
    <Extras p={p} />
    <div className="flex justify-between gap-4 py-1.5">
      <dt className="text-xs text-slate-500 uppercase tracking-widest">Received</dt>
      <dd className="text-sm text-slate-200">{p.received_at ? new Date(p.received_at).toLocaleString() : "—"}</dd>
    </div>
    {p.body && <div className="mt-3 text-sm text-slate-300 whitespace-pre-wrap bg-slate-950/60 border border-slate-800 rounded-md p-3">{p.body}</div>}
    {p.edited_by_name && <div className="mt-2 text-xs text-slate-500">Last edited by {p.edited_by_name} · {new Date(p.edited_at).toLocaleString()}</div>}
  </dl>
);

export const PreAlertPanel = ({ sh, user, onSaved }) => {
  const p = sh.pre_alert || {};
  const canEdit = BROKER_ROLES.includes(user?.role);
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ ...Object.fromEntries(FIELDS.map(([k]) => [k, p[k] || ""])), body: p.body || "", shipment_type: sh.type || "brokerage" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try { await api.put(`/shipments/${sh.id}/pre-alert`, f); toast.success("Pre-alert updated"); setEditing(false); onSaved(); }
    catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="prealert-panel" className="glass-card rounded-lg p-6">
      <div className="flex items-center justify-between mb-5 gap-4">
        <div className="flex items-center gap-2 font-display text-xl font-bold"><Mail size={18} className="text-sky-400" /> Pre-Alert Overview</div>
        {canEdit && !editing && (
          <Button data-testid="button-edit-prealert" size="sm" variant="outline" onClick={() => setEditing(true)} className="border-slate-700 hover:bg-slate-800">
            <Pencil size={13} className="mr-1.5" /> Edit
          </Button>
        )}
      </div>
      {!editing ? <View p={p} /> : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {FIELDS.map(([k, l]) => (
              <div key={k}><Label>{l}</Label>
                <Input data-testid={`input-prealert-edit-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="bg-slate-950 border-slate-700" />
              </div>
            ))}
            <div><Label>Folder</Label>
              <Select value={f.shipment_type} onValueChange={(v) => setF({ ...f, shipment_type: v })} disabled={sh.current_stage !== "pre_alert"}>
                <SelectTrigger data-testid="select-prealert-folder" className="bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {SHIPMENT_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Body</Label>
            <Textarea data-testid="input-prealert-edit-body" rows={5} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="bg-slate-950 border-slate-700" />
          </div>
          <div className="flex gap-2">
            <Button data-testid="button-save-prealert" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-500">
              {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />} Save
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)} className="hover:bg-slate-800">Cancel</Button>
          </div>
        </div>
      )}
      {!canEdit && <div className="mt-4 text-xs text-slate-500">Visible to everyone; only brokers can edit.</div>}
    </div>
  );
};
